/**
 * MCP Skills API - 完全独立的 API 层
 *
 * 与工具搜索完全分离：
 * - 独立的 API 端点
 * - 独立的数据类型
 * - 独立的缓存键
 * - 不影响工具搜索功能
 */

import { safeJsonParse, sanitizeCacheKey } from '../utils/sanitize';
import { quickComplianceCheck } from '../utils/compliance';

const API_BASE_URL = "/api";

// ==================== 本地缓存 ====================

const SKILL_CACHE_STORAGE_KEY = 'psp_skills_cache';
const SKILL_SEARCH_HISTORY_KEY = 'psp_skill_search_history';

interface SkillCacheEntry {
  keyword: string;
  data: SkillSearchResult;
  createdAt: number;
  hitCount: number;
}

/**
 * 从本地缓存获取 SKILL 搜索结果
 */
function getFromLocalCache(keyword: string): SkillSearchResult | null {
  try {
    const cacheJson = localStorage.getItem(SKILL_CACHE_STORAGE_KEY);
    if (!cacheJson) return null;

    const cache: Record<string, SkillCacheEntry> = safeJsonParse(cacheJson, {});

    // 使用安全的缓存键
    const safeKey = sanitizeCacheKey(keyword);
    const entry = cache[safeKey];

    if (entry) {
      // 更新命中次数
      entry.hitCount++;
      cache[safeKey] = entry;
      localStorage.setItem(SKILL_CACHE_STORAGE_KEY, JSON.stringify(cache));

      return {
        ...entry.data,
        fromCache: true
      };
    }

    return null;
  } catch (error) {
    console.error('SKILL缓存读取失败:', error);
    return null;
  }
}

/**
 * 保存 SKILL 搜索结果到本地缓存
 */
function saveToLocalCache(keyword: string, data: SkillSearchResult): void {
  try {
    const cacheJson = localStorage.getItem(SKILL_CACHE_STORAGE_KEY) || '{}';
    const cache: Record<string, SkillCacheEntry> = safeJsonParse(cacheJson, {});

    // 使用安全的缓存键
    const safeKey = sanitizeCacheKey(keyword);
    cache[safeKey] = {
      keyword: safeKey,
      data: data,
      createdAt: Date.now(),
      hitCount: 0
    };

    localStorage.setItem(SKILL_CACHE_STORAGE_KEY, JSON.stringify(cache));
  } catch (error) {
    console.error('SKILL缓存保存失败:', error);
  }
}

// ==================== 类型定义 ====================

/**
 * MCP Skill 数据结构
 */
export interface Skill {
  id: string;
  name: string;
  publisher: string;
  description: string;
  categories: string[];
  tags: string[];
  homepage: string;
  repository: string;
  license: string;
  rating: number;
  downloads: string;
  useCases?: string[];
  installation?: string;
  quickStart?: string;
}

/**
 * SKILL 搜索结果
 */
export interface SkillSearchResult {
  searchIntent: string;
  originalQuery: string;
  resultCount: number;
  searchTime: string;
  results: Skill[];
  relatedSkills?: {
    name: string;
    category: string;
    reason: string;
  }[];
  error?: string;
  fromCache?: boolean;
}

// ==================== API 函数 ====================

/**
 * 搜索 MCP Skills
 *
 * @param query - 搜索关键词
 * @param lang - 语言 ('zh' | 'en')
 * @param useLLM - 是否使用 LLM 搜索（默认 false，使用本地数据）
 * @returns SkillSearchResult
 */
export const searchSkills = async (
  query: string,
  lang: 'zh' | 'en' = 'zh',
  useLLM: boolean = false
): Promise<SkillSearchResult | null> => {

  if (!query || query.trim() === '') {
    return null;
  }

  // 前端快速合规性检查（实时反馈）
  const complianceCheck = quickComplianceCheck(query);
  if (!complianceCheck.valid) {
    // 返回错误结果，不发送请求
    return {
      searchIntent: lang === 'zh' ? '技能搜索' : 'Skill Search',
      originalQuery: query,
      resultCount: 0,
      searchTime: '0s',
      results: [],
      error: complianceCheck.warning || (lang === 'zh' ? '查询内容不合规' : 'Query does not comply with usage guidelines')
    };
  }

  // 1. 先查本地缓存（快速返回，不请求服务器）
  const cached = getFromLocalCache(query);
  if (cached) {
    return cached;
  }

  // 2. 请求服务器API
  try {
    const params = new URLSearchParams({
      q: query,
      lang: lang,
      llm: useLLM.toString()
    });

    const response = await fetch(`${API_BASE_URL}/search/skill?${params}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();

    // 3. 保存到本地缓存（包括服务器缓存命中的结果）
    saveToLocalCache(query, data);

    return data;
  } catch (error) {
    console.log(`   Message: ${(error as Error).message}`);

    return {
      searchIntent: '技能搜索',
      originalQuery: query,
      resultCount: 0,
      searchTime: '0s',
      results: [],
      error: '搜索请求失败'
    };
  }
};

/**
 * 获取热门 Skills（推荐列表）
 *
 * @returns Skill[]
 */
export const getPopularSkills = async (): Promise<Skill[]> => {
  // 暂时返回空列表，后续可以从 API 获取
  return [];
};

/**
 * 根据 ID 获取 Skill 详情
 *
 * @param id - Skill ID
 * @returns Skill | null
 */
export const getSkillById = async (id: string): Promise<Skill | null> => {

  // 暂时返回 null，后续可以实现
  return null;
};

// ==================== 辅助函数 ====================

/**
 * 格式化下载量
 */
export const formatDownloads = (downloads: string): string => {
  return downloads;
};

/**
 * 获取评分星星显示
 */
export const getRatingStars = (rating: number): string => {
  const fullStars = Math.floor(rating);
  const hasHalfStar = rating % 1 >= 0.5;
  const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

  return '⭐'.repeat(fullStars) +
         (hasHalfStar ? '✨' : '') +
         '☆'.repeat(emptyStars);
};

/**
 * 根据 ID 获取安装命令
 */
export const getInstallCommand = (skill: Skill): string => {
  if (skill.installation) {
    return skill.installation;
  }

  return `npx @modelcontextprotocol/server-${skill.id}`;
};
